import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
} from "@sportscanner/ui";
import { clubLimitsSchema } from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import { Section, Row } from "@/components/settings-form";

// Only the fields the Limits page reads. The gating flags (requireLogin /
// collect* / allowMemberOnlySlots / allowPrimeTimeSlots) decide which limit
// blocks can bind, and are read from the SAVED club (they're edited elsewhere:
// Access & contact, and Time slots).
interface ClubLimitData {
  id: string;
  requireLogin: boolean;
  collectPhone: boolean;
  collectEmail: boolean;
  allowMemberOnlySlots: boolean;
  allowPrimeTimeSlots: boolean;
  limitBookingsPerContactPerDay: boolean;
  maxBookingsPerContactPerDay: number;
  maxBookingsPerCreationDay: number | null;
  enforceBookingQuotas: boolean;
  maxBookingsPerMemberPerDay: number | null;
  preventConsecutiveDayBooking: boolean;
  lateBookingExemptionHours: number | null;
  maxPrimeBookingsPerWeek: number | null;
  requirePlayerRoster: boolean;
}

interface Props {
  clubId: string;
}

export default function LimitsForm({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId, "limits"],
    queryFn: async () => {
      const data = await apiGet<{ club: ClubLimitData }>(
        `/api/admin/clubs/${clubId}`,
      );
      return data.club;
    },
  });
  const club = clubQuery.data;

  const [limitBookingsPerContactPerDay, setLimitBookingsPerContactPerDay] =
    useState(true);
  const [maxBookingsPerContactPerDay, setMaxBookingsPerContactPerDay] =
    useState("2");
  const [maxBookingsPerCreationDay, setMaxBookingsPerCreationDay] =
    useState("");
  const [enforceBookingQuotas, setEnforceBookingQuotas] = useState(false);
  const [maxBookingsPerMemberPerDay, setMaxBookingsPerMemberPerDay] =
    useState("1");
  const [preventConsecutiveDayBooking, setPreventConsecutiveDayBooking] =
    useState(false);
  const [lateBookingExemptionHours, setLateBookingExemptionHours] =
    useState("");
  const [maxPrimeBookingsPerWeek, setMaxPrimeBookingsPerWeek] = useState("");
  const [requirePlayerRoster, setRequirePlayerRoster] = useState(false);

  // Reset when the loaded club changes (e.g. SUPERADMIN picks a different one).
  useEffect(() => {
    if (!club) return;
    setLimitBookingsPerContactPerDay(club.limitBookingsPerContactPerDay);
    setMaxBookingsPerContactPerDay(String(club.maxBookingsPerContactPerDay));
    setMaxBookingsPerCreationDay(
      club.maxBookingsPerCreationDay !== null
        ? String(club.maxBookingsPerCreationDay)
        : "",
    );
    setEnforceBookingQuotas(club.enforceBookingQuotas);
    setMaxBookingsPerMemberPerDay(
      club.maxBookingsPerMemberPerDay !== null
        ? String(club.maxBookingsPerMemberPerDay)
        : "1",
    );
    setPreventConsecutiveDayBooking(club.preventConsecutiveDayBooking);
    setLateBookingExemptionHours(
      club.lateBookingExemptionHours !== null
        ? String(club.lateBookingExemptionHours)
        : "",
    );
    setMaxPrimeBookingsPerWeek(
      club.maxPrimeBookingsPerWeek !== null
        ? String(club.maxPrimeBookingsPerWeek)
        : "",
    );
    setRequirePlayerRoster(club.requirePlayerRoster);
    setErrorCode(null);
  }, [club]);

  const maxContactNum = Number.parseInt(maxBookingsPerContactPerDay, 10);
  const maxContactInvalid =
    limitBookingsPerContactPerDay &&
    (!Number.isFinite(maxContactNum) ||
      maxContactNum < 1 ||
      maxContactNum > 100);

  const creationDayNum = maxBookingsPerCreationDay.trim()
    ? Number.parseInt(maxBookingsPerCreationDay, 10)
    : null;
  const creationDayInvalid =
    creationDayNum !== null &&
    (!Number.isFinite(creationDayNum) ||
      creationDayNum < 1 ||
      creationDayNum > 100);

  const memberDailyNum = Number.parseInt(maxBookingsPerMemberPerDay, 10);
  const lateExemptionNum = lateBookingExemptionHours.trim()
    ? Number.parseInt(lateBookingExemptionHours, 10)
    : null;
  const primeWeeklyNum = maxPrimeBookingsPerWeek.trim()
    ? Number.parseInt(maxPrimeBookingsPerWeek, 10)
    : null;
  const memberQuotaSectionInvalid =
    enforceBookingQuotas &&
    (!Number.isFinite(memberDailyNum) ||
      memberDailyNum < 1 ||
      memberDailyNum > 100 ||
      (lateExemptionNum !== null &&
        (lateExemptionNum < 1 || lateExemptionNum > 72)) ||
      (!!club?.allowPrimeTimeSlots &&
        primeWeeklyNum !== null &&
        (!Number.isFinite(primeWeeklyNum) ||
          primeWeeklyNum < 1 ||
          primeWeeklyNum > 100)));

  const isValid =
    !maxContactInvalid && !creationDayInvalid && !memberQuotaSectionInvalid;

  // Each block keys on an identity the booking must carry, so only show the
  // ones whose identity the club actually collects (mirrors the server gates).
  // Read from the SAVED club flags — these features are edited on other pages.
  const hasContactChannel = !!club?.collectPhone || !!club?.collectEmail;
  const canIdentifyBooker = hasContactChannel || !!club?.requireLogin;
  const showCreationDayLimit = canIdentifyBooker;
  const showContactLimit = hasContactChannel && !club?.requireLogin;
  const showMemberLimit = !!club?.requireLogin || !!club?.allowMemberOnlySlots;
  const showIdentityHint =
    !showCreationDayLimit && !showContactLimit && !showMemberLimit;

  const dirty = useMemo(() => {
    if (!club) return false;
    return (
      limitBookingsPerContactPerDay !== club.limitBookingsPerContactPerDay ||
      (Number.isFinite(maxContactNum) &&
        maxContactNum !== club.maxBookingsPerContactPerDay) ||
      (creationDayNum ?? null) !== (club.maxBookingsPerCreationDay ?? null) ||
      enforceBookingQuotas !== club.enforceBookingQuotas ||
      (Number.isFinite(memberDailyNum)
        ? memberDailyNum !== (club.maxBookingsPerMemberPerDay ?? 0)
        : club.maxBookingsPerMemberPerDay !== null) ||
      preventConsecutiveDayBooking !== club.preventConsecutiveDayBooking ||
      (lateExemptionNum ?? null) !== (club.lateBookingExemptionHours ?? null) ||
      (primeWeeklyNum ?? null) !== (club.maxPrimeBookingsPerWeek ?? null) ||
      requirePlayerRoster !== club.requirePlayerRoster
    );
  }, [
    club,
    limitBookingsPerContactPerDay,
    maxContactNum,
    creationDayNum,
    enforceBookingQuotas,
    memberDailyNum,
    preventConsecutiveDayBooking,
    lateExemptionNum,
    primeWeeklyNum,
    requirePlayerRoster,
  ]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!club) throw new Error("no club");
      const parsed = clubLimitsSchema.safeParse({
        limitBookingsPerContactPerDay,
        maxBookingsPerContactPerDay: Number.isFinite(maxContactNum)
          ? maxContactNum
          : club.maxBookingsPerContactPerDay,
        maxBookingsPerCreationDay: creationDayNum,
        enforceBookingQuotas,
        maxBookingsPerMemberPerDay: Number.isFinite(memberDailyNum)
          ? memberDailyNum
          : club.maxBookingsPerMemberPerDay,
        preventConsecutiveDayBooking,
        lateBookingExemptionHours: lateExemptionNum,
        maxPrimeBookingsPerWeek: primeWeeklyNum,
        requirePlayerRoster,
      });
      if (!parsed.success) {
        throw {
          error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED",
        };
      }
      await apiPut(`/api/admin/clubs/${club.id}/limits`, parsed.data);
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("limits.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId],
      });
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  if (clubQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }
  if (clubQuery.isError || !club) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{t("errors.api.INTERNAL_ERROR")}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      {showIdentityHint && (
        <p className="text-sm text-neutral-500 leading-relaxed">
          {t("clubs.settings.limits.identityHint")}
        </p>
      )}

      {showCreationDayLimit && (
        <Section title={t("clubs.settings.limits.perUserSection")}>
          <Label
            htmlFor="lf-max-creation-day"
            className="text-sm text-neutral-700"
          >
            {t("clubs.settings.limits.maxPerCreationDay.label")}
          </Label>
          <p className="text-xs text-neutral-500">
            {t("clubs.settings.limits.maxPerCreationDay.hint")}
          </p>
          <Input
            id="lf-max-creation-day"
            type="number"
            min={1}
            max={100}
            step={1}
            placeholder={t(
              "clubs.settings.limits.maxPerCreationDay.placeholder",
            )}
            value={maxBookingsPerCreationDay}
            onChange={(e) => setMaxBookingsPerCreationDay(e.target.value)}
            disabled={mutation.isPending}
            aria-invalid={creationDayInvalid}
            className={`rounded-lg border-neutral-200 w-32 ${
              creationDayInvalid ? "border-red-400" : ""
            }`}
          />
          {creationDayInvalid && (
            <p className="text-xs text-red-500">
              {t("errors.api.BOOKING_LIMIT_INVALID")}
            </p>
          )}
        </Section>
      )}

      {showContactLimit && (
        <Section title={t("clubs.settings.limits.contactSection")}>
          <Row
            id="lf-limit-contact"
            label={t("clubs.editDialog.limitBookings.label")}
            hint={t("clubs.editDialog.limitBookings.hint")}
            checked={limitBookingsPerContactPerDay}
            onCheckedChange={setLimitBookingsPerContactPerDay}
            disabled={mutation.isPending}
          />
          {limitBookingsPerContactPerDay && (
            <div className="space-y-2 pl-4 border-l-2 border-neutral-100">
              <Label
                htmlFor="lf-max-contact"
                className="text-sm text-neutral-700"
              >
                {t("clubs.editDialog.maxBookings.label")}
              </Label>
              <Input
                id="lf-max-contact"
                type="number"
                min={1}
                max={100}
                step={1}
                value={maxBookingsPerContactPerDay}
                onChange={(e) => setMaxBookingsPerContactPerDay(e.target.value)}
                disabled={mutation.isPending}
                aria-invalid={maxContactInvalid}
                className={`rounded-lg border-neutral-200 w-32 ${
                  maxContactInvalid ? "border-red-400" : ""
                }`}
              />
              {maxContactInvalid && (
                <p className="text-xs text-red-500">
                  {t("errors.api.BOOKING_LIMIT_INVALID")}
                </p>
              )}
            </div>
          )}
        </Section>
      )}

      {showMemberLimit && (
        <Section title={t("clubs.settings.limits.memberSection")}>
          <Row
            id="lf-enforce-quotas"
            label={t("clubs.settings.limits.enforce.label")}
            hint={t("clubs.settings.limits.enforce.hint")}
            checked={enforceBookingQuotas}
            onCheckedChange={setEnforceBookingQuotas}
            disabled={mutation.isPending}
          />
          {enforceBookingQuotas && (
            <div className="pl-4 border-l-2 border-neutral-100 space-y-4">
              <div className="space-y-2">
                <Label
                  htmlFor="lf-max-member-day"
                  className="text-sm text-neutral-700"
                >
                  {t("clubs.settings.limits.maxPerMemberPerDay.label")}
                </Label>
                <p className="text-xs text-neutral-500">
                  {t("clubs.settings.limits.maxPerMemberPerDay.hint")}
                </p>
                <Input
                  id="lf-max-member-day"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={maxBookingsPerMemberPerDay}
                  onChange={(e) =>
                    setMaxBookingsPerMemberPerDay(e.target.value)
                  }
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200 w-32"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="lf-late-exemption"
                  className="text-sm text-neutral-700"
                >
                  {t("clubs.settings.limits.lateExemption.label")}
                </Label>
                <p className="text-xs text-neutral-500">
                  {t("clubs.settings.limits.lateExemption.hint")}
                </p>
                <Input
                  id="lf-late-exemption"
                  type="number"
                  min={1}
                  max={72}
                  step={1}
                  placeholder={t(
                    "clubs.settings.limits.lateExemption.placeholder",
                  )}
                  value={lateBookingExemptionHours}
                  onChange={(e) => setLateBookingExemptionHours(e.target.value)}
                  disabled={mutation.isPending}
                  className="rounded-lg border-neutral-200 w-32"
                />
              </div>

              <Row
                id="lf-prevent-consecutive"
                label={t("clubs.settings.limits.preventConsecutive.label")}
                hint={t("clubs.settings.limits.preventConsecutive.hint")}
                checked={preventConsecutiveDayBooking}
                onCheckedChange={setPreventConsecutiveDayBooking}
                disabled={mutation.isPending}
              />

              {club.allowPrimeTimeSlots && (
                <div className="space-y-2">
                  <Label
                    htmlFor="lf-prime-week"
                    className="text-sm text-neutral-700"
                  >
                    {t("clubs.settings.limits.maxPrimePerWeek.label")}
                  </Label>
                  <p className="text-xs text-neutral-500">
                    {t("clubs.settings.limits.maxPrimePerWeek.hint")}
                  </p>
                  <Input
                    id="lf-prime-week"
                    type="number"
                    min={1}
                    max={100}
                    step={1}
                    placeholder={t(
                      "clubs.settings.limits.maxPrimePerWeek.placeholder",
                    )}
                    value={maxPrimeBookingsPerWeek}
                    onChange={(e) => setMaxPrimeBookingsPerWeek(e.target.value)}
                    disabled={mutation.isPending}
                    className="rounded-lg border-neutral-200 w-32"
                  />
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      <Section title={t("clubs.settings.limits.rosterSection")}>
        <Row
          id="lf-require-roster"
          label={t("clubs.settings.limits.requireRoster.label")}
          hint={t("clubs.settings.limits.requireRoster.hint")}
          checked={requirePlayerRoster}
          onCheckedChange={setRequirePlayerRoster}
          disabled={mutation.isPending}
        />
      </Section>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!isValid || !dirty || mutation.isPending}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t("common.save")
          )}
        </Button>
      </div>
    </div>
  );
}
